'use client';

import React, { useState, useEffect } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Channel {
  id: number;
  emoji: string;
  name: string;
  genre: string;
  listeners: string;
  color: string;
  span?: number;
}

interface Feature {
  emoji: string;
  title: string;
  description: string;
  color: string;
  badge?: string;
  large?: boolean;
}

interface LiveEvent {
  id: number;
  image: string;
  alt: string;
  tag: string;
  tagColor: string;
  title: string;
  host: string;
  time: string;
}

interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  avatar: string;
  avatarAlt: string;
}

interface PricingPlan {
  id: number;
  name: string;
  subtitle: string;
  price: string;
  features: string[];
  cta: string;
  featured?: boolean;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const channels: Channel[] = [
{ id: 1, emoji: '🎵', name: 'Neon Pulse', genre: 'Electronic', listeners: '31.2K', color: '#7C3AED', span: 2 },
{ id: 2, emoji: '🌙', name: 'Midnight Soul', genre: 'R&B / Soul', listeners: '22.4K', color: '#F59E0B' },
{ id: 3, emoji: '🎤', name: 'Street Cipher', genre: 'Hip-Hop', listeners: '28.7K', color: '#EF4444' },
{ id: 4, emoji: '🌿', name: 'Green Horizon', genre: 'Indie / Folk', listeners: '14.1K', color: '#10B981' },
{ id: 5, emoji: '🎷', name: 'Blue Note', genre: 'Jazz & Blues', listeners: '11.3K', color: '#3B82F6' },
{ id: 6, emoji: '🔥', name: 'Ignite', genre: 'Pop Hits', listeners: '47.8K', color: '#EC4899', span: 2 },
{ id: 7, emoji: '🤠', name: 'Dusty Roads', genre: 'Country', listeners: '16.5K', color: '#D97706' },
{ id: 8, emoji: '🎙️', name: 'Open Mic', genre: 'Talk & Debate', listeners: '9.6K', color: '#6366F1' }];


const genres = ['Electronic', 'Hip-Hop', 'R&B', 'Jazz', 'Pop', 'Country', 'Indie', 'Classical', 'Reggae', 'Latin', 'Rock', 'Blues', 'Podcasts', 'Sports', 'News', 'Comedy'];

const features: Feature[] = [
{ emoji: '🎧', title: 'Crystal Clear Audio', description: 'Studio-grade streaming up to 24-bit/320kbps. Every note, every breath — exactly as the artist intended.', color: '#F5A623', badge: '24-bit / 320kbps', large: true },
{ emoji: '📡', title: 'Always Live', description: 'Real-time broadcasts from studios across the globe, 24 hours a day, 7 days a week.', color: '#7C3AED' },
{ emoji: '🌍', title: 'Global Stations', description: 'Tune into curated stations from 70+ countries and counting.', color: '#10B981' },
{ emoji: '🎙️', title: 'Exclusive Shows', description: 'Artist sessions, live interviews, and performances you won\'t find anywhere else.', color: '#3B82F6' },
{ emoji: '📱', title: 'Every Device', description: 'Phone, tablet, desktop, smart TV, car — seamless across all your screens.', color: '#EC4899' }];


const liveEvents: LiveEvent[] = [
{
  id: 1,
  image: "https://images.unsplash.com/photo-1564316977905-221efcd0a1f3",
  alt: 'DJ performing live at an electronic music festival with colorful stage lights',
  tag: 'Electronic',
  tagColor: '#7C3AED',
  title: 'Neon Frequencies',
  host: 'DJ Axiom',
  time: 'Tonight · 10 PM EST'
},
{
  id: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15c6b8374-1772293467804.png",
  alt: 'Female R&B singer performing on a dimly lit stage with warm spotlight',
  tag: 'R&B / Soul',
  tagColor: '#F59E0B',
  title: 'Velvet Sessions',
  host: 'Zara Monroe',
  time: 'Tomorrow · 9 PM EST'
},
{
  id: 3,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15ea75693-1774049123813.png",
  alt: 'Hip-hop artist performing live on stage with crowd in the background',
  tag: 'Hip-Hop',
  tagColor: '#EF4444',
  title: 'Block Party Live',
  host: 'Verse & Remy',
  time: 'Sat · 8 PM EST'
}];


const testimonials: Testimonial[] = [
{
  id: 1,
  quote: 'WaveFlow completely transformed how I discover music. The curation feels personal — like someone actually knows my taste.',
  name: 'Priya Nair',
  role: 'Music Blogger, London',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80',
  avatarAlt: 'Priya Nair smiling profile photo'
},
{
  id: 2,
  quote: 'I drive 90 minutes each way to work. WaveFlow makes every commute something I actually look forward to. The talk shows are brilliant.',
  name: 'Marcus Webb',
  role: 'Software Engineer, Austin',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80',
  avatarAlt: 'Marcus Webb professional headshot'
},
{
  id: 3,
  quote: 'The audio quality on the Pro plan is jaw-dropping. I can hear details in tracks I\'ve been listening to for years. Absolutely worth it.',
  name: 'Camille Fontaine',
  role: 'Audiophile & Producer, Paris',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80',
  avatarAlt: 'Camille Fontaine smiling portrait'
}];


const pricingPlans: PricingPlan[] = [
{
  id: 1,
  name: 'Starter',
  subtitle: 'Perfect for casual listeners',
  price: '$4.99',
  features: ['60+ curated stations', 'Mobile & web access', 'Standard audio quality', 'Basic on-demand library'],
  cta: 'Start Free Trial'
},
{
  id: 2,
  name: 'Studio',
  subtitle: 'For the true audio enthusiast',
  price: '$11.99',
  features: ['300+ stations & channels', 'Crystal clear audio quality', 'Offline listening', 'Exclusive live shows', 'Ad-free experience'],
  cta: 'Get Studio',
  featured: true
},
{
  id: 3,
  name: 'Pro',
  subtitle: 'Everything, everywhere',
  price: '$18.99',
  features: ['Unlimited stations', 'Multi-device streaming', 'Lossless + Spatial audio', 'Priority event access', 'Family sharing (5 users)'],
  cta: 'Go Pro'
}];


const waveBars = [40, 70, 55, 85, 60, 90, 45, 75, 50, 80];

// ─── Sub-components ───────────────────────────────────────────────────────────
function StarRating() {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) =>
      <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#F5A623">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      )}
    </div>);

}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#F5A623" className="flex-shrink-0">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>);

}

function PhoneIcon({ size = 14 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
    </svg>);

}

function MusicNoteIcon({ size = 18 }: {size?: number;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="#0D0D1A">
      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
    </svg>);

}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const heroChannels = channels.slice(0, 4);
  const currentHeroChannel = heroChannels[activeChannel] || heroChannels[0];

  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main>
      {/* ── Navigation ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: navScrolled ? 'rgba(13,13,26,0.92)' : 'transparent',
          backdropFilter: navScrolled ? 'blur(16px)' : 'none',
          borderBottom: navScrolled ? '1px solid rgba(255,255,255,0.06)' : 'none'
        }}>
        
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2.5 no-underline">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F5A623, #D4891A)' }}>
              
              <MusicNoteIcon size={18} />
            </div>
            <span className="font-display font-black text-lg tracking-tight text-white" style={{ fontWeight: 800 }}>
              Wave<span style={{ color: '#F5A623' }}>Flow</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a href="#channels" className="wf-nav-link">Channels</a>
            <a href="#shows" className="wf-nav-link">Live Shows</a>
            <a href="#podcasts" className="wf-nav-link">Podcasts</a>
            <a href="#plans" className="wf-nav-link">Plans</a>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:18888533670"
              className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
              style={{ color: '#F5A623', textDecoration: 'none' }}>
              
              <PhoneIcon size={14} />
              1-888-853-3670
            </a>
            <a href="#" className="wf-nav-link">Sign In</a>
            <a href="#plans" className="btn-sm-amber">Start Free Trial</a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.06)' }}
            aria-label="Toggle menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            
            <div className="w-5 flex flex-col gap-1">
              <span
                className="block h-0.5 bg-white rounded transition-all"
                style={{ transform: mobileMenuOpen ? 'rotate(45deg) translateY(6px)' : 'none' }} />
              
              <span
                className="block h-0.5 bg-white rounded transition-all"
                style={{ opacity: mobileMenuOpen ? 0 : 1 }} />
              
              <span
                className="block h-0.5 bg-white rounded transition-all"
                style={{ transform: mobileMenuOpen ? 'rotate(-45deg) translateY(-6px)' : 'none' }} />
              
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div
          className="md:hidden px-6 pb-6 pt-2"
          style={{ background: 'rgba(13,13,26,0.97)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          
            <div className="flex flex-col gap-2">
              <a href="#channels" className="wf-nav-link" onClick={() => setMobileMenuOpen(false)}>Channels</a>
              <a href="#shows" className="wf-nav-link" onClick={() => setMobileMenuOpen(false)}>Live Shows</a>
              <a href="#podcasts" className="wf-nav-link" onClick={() => setMobileMenuOpen(false)}>Podcasts</a>
              <a href="#plans" className="wf-nav-link" onClick={() => setMobileMenuOpen(false)}>Plans</a>
              <div className="section-divider my-2" />
              <a href="#plans" className="btn-amber w-full justify-center" onClick={() => setMobileMenuOpen(false)}>Start Free Trial</a>
            </div>
          </div>
        }
      </nav>

      {/* ── Hero ── */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0D0D1A 0%, #1A1A2E 50%, #0D0D1A 100%)' }}>
        
        {/* Background glows */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, #F5A623, transparent)' }} />
          
          <div
            className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }} />
          
          <div
            className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full opacity-10 blur-3xl"
            style={{ background: 'radial-gradient(circle, #3B82F6, transparent)', transform: 'translate(-50%,-50%)' }} />
          
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Copy */}
            <div>
              <div className="wf-badge mb-6">
                <span>🎵</span>
                <span>500+ Live Channels</span>
              </div>

              <h1
                className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-none tracking-tight mb-6"
                style={{ fontWeight: 900 }}>
                
                Your sound,<br />
                <span className="gradient-text">your universe.</span>
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: 'rgba(240,238,248,0.65)', maxWidth: 480 }}>
                
                WaveFlow delivers hand-curated music, live shows, and on-demand podcasts — all in one place. No algorithms. Just great sound.
              </p>

              {/* Phone CTA */}
              <a
                href="tel:18888533670"
                className="inline-flex items-center gap-3 rounded-2xl px-5 py-3 mb-8 transition-all"
                style={{
                  background: 'rgba(245,166,35,0.12)',
                  border: '1px solid rgba(245,166,35,0.3)',
                  textDecoration: 'none'
                }}>
                
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #F5A623, #D4891A)' }}>
                  
                  <PhoneIcon size={16} />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest mb-0.5" style={{ color: 'rgba(240,238,248,0.5)' }}>
                    Call Us Free · 24/7
                  </div>
                  <div className="font-display font-black text-lg" style={{ color: '#F5A623', fontWeight: 900 }}>
                    1-888-853-3670
                  </div>
                </div>
              </a>

              {/* Buttons */}
              <div className="flex flex-wrap gap-4 mb-12">
                <a href="#plans" className="btn-amber">
                  Start Listening Free
                  <svg className="ml-2" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </a>
                <a href="#channels" className="btn-ghost">Explore Channels</a>
              </div>

              {/* Stats */}
              <div className="flex gap-8">
                {[
                { value: '500+', label: 'Live Channels' },
                { value: '6M+', label: 'Active Listeners' },
                { value: '80+', label: 'Genres' }].
                map((stat) =>
                <div key={stat.label}>
                    <div className="font-display text-2xl font-black" style={{ color: '#F5A623', fontWeight: 900 }}>{stat.value}</div>
                    <div className="text-sm" style={{ color: 'rgba(240,238,248,0.5)' }}>{stat.label}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Player Card */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-sm">
                <div
                  className="wf-card-flat p-6 relative"
                  style={{ background: 'linear-gradient(145deg, #1E1E32, #252540)' }}>
                  
                  {/* Live badge + listeners */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="wf-badge wf-badge-green">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      LIVE NOW
                    </div>
                    <span className="text-xs" style={{ color: 'rgba(240,238,248,0.4)' }}>
                      {currentHeroChannel?.listeners} listening
                    </span>
                  </div>

                  {/* Album art */}
                  <div
                    className="w-full aspect-square rounded-2xl mb-5 flex items-center justify-center text-6xl"
                    style={{
                      background: `linear-gradient(135deg, ${currentHeroChannel?.color}33 0%, ${currentHeroChannel?.color}11 100%)`,
                      border: `1px solid ${currentHeroChannel?.color}33`
                    }}>
                    
                    {currentHeroChannel?.emoji}
                  </div>

                  {/* Track info */}
                  <div className="mb-4">
                    <div className="font-display font-bold text-xl text-white mb-1">{currentHeroChannel?.name}</div>
                    <div className="text-sm" style={{ color: 'rgba(240,238,248,0.5)' }}>{currentHeroChannel?.genre}</div>
                  </div>

                  {/* Wave bars */}
                  <div className="mb-5">
                    <div className="flex items-end gap-[3px] h-8">
                      {waveBars.map((h, i) =>
                      <div
                        key={i}
                        className="wave-bar"
                        style={{
                          height: `${h}%`,
                          animationDelay: `${i * 0.12}s`,
                          animationPlayState: isPlaying ? 'running' : 'paused'
                        }} />

                      )}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center justify-between">
                    <button
                      className="p-2 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.06)' }}
                      aria-label="Previous"
                      onClick={() => setActiveChannel((prev) => (prev - 1 + heroChannels.length) % heroChannels.length)}>
                      
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(240,238,248,0.6)">
                        <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                      </svg>
                    </button>
                    <button
                      className="w-14 h-14 rounded-full flex items-center justify-center animate-pulse-amber"
                      style={{ background: 'linear-gradient(135deg, #F5A623, #D4891A)' }}
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                      onClick={() => setIsPlaying(!isPlaying)}>
                      
                      {isPlaying ?
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#0D0D1A">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                        </svg> :

                      <svg width="22" height="22" viewBox="0 0 24 24" fill="#0D0D1A">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      }
                    </button>
                    <button
                      className="p-2 rounded-full"
                      style={{ background: 'rgba(255,255,255,0.06)' }}
                      aria-label="Next"
                      onClick={() => setActiveChannel((prev) => (prev + 1) % heroChannels.length)}>
                      
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="rgba(240,238,248,0.6)">
                        <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                      </svg>
                    </button>
                  </div>

                  {/* Dots */}
                  <div className="flex justify-center gap-2 mt-5">
                    {heroChannels.map((_, i) =>
                    <button
                      key={i}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: i === activeChannel ? 20 : 6,
                        height: 6,
                        background: i === activeChannel ? '#F5A623' : 'rgba(255,255,255,0.2)'
                      }}
                      aria-label={`Channel ${i + 1}`}
                      onClick={() => setActiveChannel(i)} />

                    )}
                  </div>
                </div>

                {/* Floating cards */}
                <div
                  className="absolute -left-12 top-16 wf-card-flat px-3 py-2 hidden lg:flex items-center gap-2"
                  style={{ animation: 'float 5s ease-in-out infinite' }}>
                  
                  <span className="text-lg">🎷</span>
                  <div>
                    <div className="text-xs font-semibold text-white">Blue Note</div>
                    <div className="text-xs" style={{ color: 'rgba(240,238,248,0.4)' }}>Jazz & Blues</div>
                  </div>
                </div>
                <div
                  className="absolute -right-10 bottom-24 wf-card-flat px-3 py-2 hidden lg:flex items-center gap-2"
                  style={{ animation: 'float 7s ease-in-out infinite 1s' }}>
                  
                  <span className="text-lg">🔥</span>
                  <div>
                    <div className="text-xs font-semibold text-white">Ignite</div>
                    <div className="text-xs" style={{ color: 'rgba(240,238,248,0.4)' }}>47.8K live</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Marquee ── */}
      <div className="py-5 overflow-hidden" style={{ background: '#F5A623' }}>
        <div className="marquee-track">
          {[...genres, ...genres].map((g, i) =>
          <span
            key={i}
            className="flex items-center gap-4 px-6 font-display font-bold text-sm uppercase tracking-widest"
            style={{ color: '#0D0D1A', whiteSpace: 'nowrap' }}>
            
              {g}
              <span style={{ color: 'rgba(13,13,26,0.3)' }}>◆</span>
            </span>
          )}
        </div>
      </div>

      {/* ── Channels ── */}
      <section id="channels" className="py-24 px-6" style={{ background: '#0D0D1A' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="wf-badge mb-4">🎚️ Featured Channels</div>
              <h2 className="font-display text-4xl md:text-5xl font-black leading-tight" style={{ fontWeight: 900 }}>
                Sound for every<br />
                <span className="gradient-text">mood &amp; moment</span>
              </h2>
            </div>
            <a href="#" className="btn-ghost self-start md:self-auto">View all 500+ channels →</a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {channels.map((ch) =>
            <div
              key={ch.id}
              className="wf-card cursor-pointer group"
              style={{
                gridColumn: ch.span ? `span ${ch.span}` : undefined
              }}>
              
                <div
                className="p-6 h-full min-h-[140px] flex flex-col justify-between relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${ch.color}22 0%, transparent 60%)` }}>
                
                  <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: ch.color }} />
                  <div className="flex items-start justify-between">
                    <span className="text-3xl">{ch.emoji}</span>
                    <div className="wf-badge wf-badge-green opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                      LIVE
                    </div>
                  </div>
                  <div>
                    <div
                    className="font-display font-bold text-white mb-1"
                    style={{ fontSize: ch.span === 2 ? 20 : 16 }}>
                    
                      {ch.name}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: ch.color }}>{ch.genre}</span>
                      <span className="text-xs" style={{ color: 'rgba(240,238,248,0.4)' }}>{ch.listeners}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Features / Why WaveFlow ── */}
      <section
        className="py-24 px-6"
        style={{ background: 'linear-gradient(180deg, #0D0D1A 0%, #1A1A2E 100%)' }}>
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="wf-badge mb-4 mx-auto inline-flex">⚡ Why WaveFlow</div>
            <h2 className="font-display text-4xl md:text-5xl font-black" style={{ fontWeight: 900 }}>
              Built different,<br />
              <span className="gradient-text">sounds different</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feat, idx) =>
            <div
              key={feat.title}
              className="wf-card cursor-pointer"
              style={{
                gridColumn: feat.large ? 'span 2' : undefined,
                gridRow: feat.large ? 'span 2' : undefined
              }}>
              
                <div
                className="p-8 h-full flex flex-col justify-between relative overflow-hidden"
                style={{
                  minHeight: feat.large ? 280 : undefined,
                  background: `linear-gradient(135deg, ${feat.color}18 0%, transparent 60%)`
                }}>
                
                  <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: feat.color }} />
                  <div>
                    <span className={`${feat.large ? 'text-5xl' : 'text-3xl'} mb-6 block`}>{feat.emoji}</span>
                    <h3 className={`font-display font-bold text-white mb-3 ${feat.large ? 'text-2xl' : ''}`}>
                      {feat.title}
                    </h3>
                    <p
                    className="leading-relaxed"
                    style={{ color: 'rgba(240,238,248,0.6)', fontSize: feat.large ? '1rem' : '0.875rem' }}>
                    
                      {feat.description}
                    </p>
                  </div>
                  {feat.badge &&
                <div className="flex items-center gap-2 mt-6">
                      <div className="flex gap-1">
                        {[20, 35, 28, 42, 30].map((h, i) =>
                    <div
                      key={i}
                      className="w-1 rounded-full"
                      style={{ height: h, background: '#F5A623', opacity: 0.75 + i * 0.05 }} />

                    )}
                      </div>
                      <span className="text-xs font-semibold" style={{ color: '#F5A623' }}>{feat.badge}</span>
                    </div>
                }
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Live Events ── */}
      <section id="shows" className="py-24 px-6" style={{ background: '#0D0D1A' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="wf-badge mb-4">🔴 Live Shows</div>
              <h2 className="font-display text-4xl md:text-5xl font-black" style={{ fontWeight: 900 }}>
                Don&apos;t miss a<br />
                <span className="gradient-text">single beat</span>
              </h2>
            </div>
            <a href="#" className="btn-ghost self-start md:self-auto">Full schedule →</a>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {liveEvents.map((ev) =>
            <div key={ev.id} className="wf-card cursor-pointer group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                  src={ev.image}
                  alt={ev.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                
                  <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(13,13,26,0.9) 0%, transparent 60%)' }} />
                
                  <div className="absolute top-3 left-3">
                    <span className="wf-badge wf-badge-green text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      UPCOMING
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: ev.tagColor }}>{ev.tag}</span>
                    <button
                    className="w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ background: ev.tagColor }}
                    aria-label={`Play ${ev.title}`}>
                    
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-white text-lg mb-1">{ev.title}</h3>
                  <p className="text-sm mb-3" style={{ color: 'rgba(240,238,248,0.5)' }}>Hosted by {ev.host}</p>
                  <div className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(240,238,248,0.4)">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
                    </svg>
                    <span className="text-xs" style={{ color: 'rgba(240,238,248,0.5)' }}>{ev.time}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section
        className="py-24 px-6"
        style={{ background: 'linear-gradient(180deg, #1A1A2E 0%, #0D0D1A 100%)' }}>
        
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="wf-badge mb-4 mx-auto inline-flex">💬 Listener Stories</div>
            <h2 className="font-display text-4xl md:text-5xl font-black" style={{ fontWeight: 900 }}>
              Real people,<br />
              <span className="gradient-text">real sound</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) =>
            <div key={t.id} className="wf-card p-6 cursor-default">
                <StarRating />
                <p className="mt-4 mb-6 text-base leading-relaxed" style={{ color: 'rgba(240,238,248,0.7)' }}>
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                  src={t.avatar}
                  alt={t.avatarAlt}
                  className="w-10 h-10 rounded-full object-cover" />
                
                  <div>
                    <div className="font-semibold text-sm text-white">{t.name}</div>
                    <div className="text-xs" style={{ color: 'rgba(240,238,248,0.4)' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="plans" className="py-24 px-6" style={{ background: '#0D0D1A' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="wf-badge mb-4 mx-auto inline-flex">💳 Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl font-black" style={{ fontWeight: 900 }}>
              Pick your<br />
              <span className="gradient-text">perfect plan</span>
            </h2>
            <p className="mt-4 text-base" style={{ color: 'rgba(240,238,248,0.5)' }}>
              All plans include a 7-day free trial. No credit card required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan) =>
            <div key={plan.id} className={`plan-card relative${plan.featured ? ' featured' : ''}`}>
                {plan.featured &&
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="wf-badge text-xs px-4 py-1.5">⭐ Most Popular</span>
                  </div>
              }
                <div className="mb-6">
                  <h3 className="font-display font-bold text-xl text-white mb-1">{plan.name}</h3>
                  <p className="text-sm" style={{ color: 'rgba(240,238,248,0.45)' }}>{plan.subtitle}</p>
                </div>
                <div className="flex items-baseline gap-1 mb-8">
                  <span className="font-display text-4xl font-black text-white" style={{ fontWeight: 900 }}>{plan.price}</span>
                  <span className="text-sm" style={{ color: 'rgba(240,238,248,0.4)' }}>/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) =>
                <li key={f} className="flex items-center gap-3 text-sm" style={{ color: 'rgba(240,238,248,0.75)' }}>
                      <CheckIcon />
                      {f}
                    </li>
                )}
                </ul>
                <a
                href="#"
                className={plan.featured ? 'btn-amber w-full text-center' : 'btn-ghost w-full text-center'}
                style={{ display: 'flex', justifyContent: 'center' }}>
                
                  {plan.cta}
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-24 px-6"
        style={{ background: 'linear-gradient(135deg, #1A1A2E 0%, #0D0D1A 100%)' }}>
        
        <div className="max-w-4xl mx-auto text-center">
          <div
            className="rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1E1E32 0%, #252540 100%)',
              border: '1px solid rgba(245,166,35,0.2)'
            }}>
            
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 blur-3xl opacity-30"
              style={{ background: 'radial-gradient(circle, #F5A623, transparent)' }} />
            
            <div className="relative">
              <div className="text-5xl mb-6">🎧</div>
              <h2 className="font-display text-4xl md:text-5xl font-black mb-4" style={{ fontWeight: 900 }}>
                Ready to tune in?
              </h2>
              <p
                className="text-lg mb-8"
                style={{ color: 'rgba(240,238,248,0.6)', maxWidth: 480, margin: '0 auto 32px' }}>
                
                Join over 6 million listeners who&apos;ve made WaveFlow their go-to audio destination.
              </p>
              <a
                href="tel:18888533670"
                className="inline-flex items-center gap-2 rounded-2xl px-6 py-3 mb-8 font-display font-black text-xl transition-all"
                style={{
                  background: 'rgba(245,166,35,0.15)',
                  border: '1px solid rgba(245,166,35,0.4)',
                  color: '#F5A623',
                  textDecoration: 'none',
                  fontWeight: 900
                }}>
                
                <PhoneIcon size={20} />
                1-888-853-3670
              </a>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="#plans" className="btn-amber">Start Free Trial — 7 Days</a>
                <a href="#channels" className="btn-ghost">Browse Channels</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer
        className="py-12 px-6"
        style={{ background: '#080810', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #F5A623, #D4891A)' }}>
                  
                  <MusicNoteIcon size={14} />
                </div>
                <span className="font-display font-black text-base text-white" style={{ fontWeight: 800 }}>
                  Wave<span style={{ color: '#F5A623' }}>Flow</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(240,238,248,0.4)' }}>
                Premium audio streaming for every mood, moment, and genre.
              </p>
              <a
                href="tel:18888533670"
                className="inline-flex items-center gap-2 text-sm font-bold transition-colors"
                style={{ color: '#F5A623', textDecoration: 'none' }}>
                
                <PhoneIcon size={14} />
                1-888-853-3670
              </a>
            </div>

            {/* Listen */}
            <div>
              <h4 className="font-semibold text-sm text-white mb-4">Listen</h4>
              <ul className="space-y-2.5">
                {['Channels', 'Live Shows', 'Podcasts', 'On Demand'].map((item) =>
                <li key={item}>
                    <a href="#" className="text-sm transition-colors" style={{ color: 'rgba(240,238,248,0.4)', textDecoration: 'none' }}>
                      {item}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold text-sm text-white mb-4">Company</h4>
              <ul className="space-y-2.5">
                {['About', 'Careers', 'Press', 'Blog'].map((item) =>
                <li key={item}>
                    <a href="#" className="text-sm transition-colors" style={{ color: 'rgba(240,238,248,0.4)', textDecoration: 'none' }}>
                      {item}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-sm text-white mb-4">Support</h4>
              <ul className="space-y-2.5">
                {['Help Center', 'Contact Us', 'Privacy Policy', 'Terms'].map((item) =>
                <li key={item}>
                    <a href="#" className="text-sm transition-colors" style={{ color: 'rgba(240,238,248,0.4)', textDecoration: 'none' }}>
                      {item}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="section-divider mb-6" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs" style={{ color: 'rgba(240,238,248,0.3)' }}>
              © 2026 WaveFlow. All rights reserved.
            </p>
            <a
              href="tel:18888533670"
              className="flex items-center gap-1.5 text-xs font-semibold transition-colors"
              style={{ color: '#F5A623', textDecoration: 'none' }}>
              
              <PhoneIcon size={12} />
              1-888-853-3670
            </a>
            <div className="flex gap-4">
              {['Twitter', 'Instagram', 'Spotify', 'Apple'].map((s) =>
              <a
                key={s}
                href="#"
                className="text-xs transition-colors"
                style={{ color: 'rgba(240,238,248,0.3)', textDecoration: 'none' }}>
                
                  {s}
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>
    </main>);

}